import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { ethers } from 'ethers';
import Web3Modal from 'web3modal';
import WalletConnectProvider from '@walletconnect/web3-provider';
import { 
  NETWORKS, 
  ABI 
} from '../utils/contractUtils';

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

// Enhanced network switching function with better error handling
const switchNetwork = async (provider, chainId) => {
  try {
    console.log(`Attempting to switch to chain ID: ${chainId}`);
    
    // First try to switch to the network
    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${chainId.toString(16)}` }],
      });
      
      // Wait for the switch to complete and verify
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Verify the switch was successful
      const currentChainId = await provider.request({ method: 'eth_chainId' });
      const actualChainId = parseInt(currentChainId, 16);
      
      if (actualChainId !== chainId) {
        throw new Error(`Network switch verification failed. Expected ${chainId}, got ${actualChainId}`);
      }
      
      console.log(`Successfully switched to chain ID: ${chainId}`);
      return true;
    } catch (switchError) {
      console.log('Switch failed, attempting to add network:', switchError);
      
      // This error code indicates that the chain has not been added to MetaMask
      if (switchError.code === 4902) {
        const networkConfig = NETWORKS[chainId === 56 ? 'bsc' : 'ethereum'];
        
        // Try to add the network
        try {
          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: `0x${chainId.toString(16)}`,
                chainName: networkConfig.name,
                nativeCurrency: {
                  name: chainId === 56 ? 'BNB' : 'ETH',
                  symbol: chainId === 56 ? 'BNB' : 'ETH',
                  decimals: 18
                },
                rpcUrls: [networkConfig.rpcUrl],
                blockExplorerUrls: [networkConfig.blockExplorer]
              }
            ]
          });
          
          // Wait for the network to be added and switched
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Verify the switch was successful
          const currentChainId = await provider.request({ method: 'eth_chainId' });
          const actualChainId = parseInt(currentChainId, 16);
          
          if (actualChainId !== chainId) {
            throw new Error(`Network addition and switch verification failed. Expected ${chainId}, got ${actualChainId}`);
          }
          
          console.log(`Successfully added and switched to chain ID: ${chainId}`);
          return true;
        } catch (addError) {
          console.error('Error adding network:', addError);
          throw new Error(`Failed to add ${networkConfig.name} network to your wallet. Please add it manually.`);
        }
      }
      
      // Handle other switch errors
      if (switchError.code === 4001) {
        throw new Error('Network switch was rejected by user');
      }
      
      throw switchError;
    }
  } catch (error) {
    console.error('Network switch error:', error);
    throw error;
  }
};

const providerOptions = {
  walletconnect: {
    package: WalletConnectProvider,
    options: {
      rpc: {
        1: `https://mainnet.infura.io/v3/431bc747c7ca4f2db0fcea6abe1dc3b9`,
        56: 'https://bsc-dataseed.bnbchain.org',
      },
      network: 'binance',
      chainId: 56,
    },
  },
};

const web3Modal = new Web3Modal({
  cacheProvider: true,
  providerOptions,
  disableInjectedProvider: false,
});

// Add error handling for ethereum property
const handleEthereumInjection = () => {
  try {
    if (window.ethereum) {
      // If ethereum is already defined, we'll use the existing one
      console.log('Using existing ethereum provider');
    }
  } catch (error) {
    console.warn('Multiple wallet extensions detected. Using first available provider.');
  }
};

export const WalletProvider = ({ children }) => {
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [address, setAddress] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [contract, setContract] = useState(null);
  const [currentNetwork, setCurrentNetwork] = useState('bsc');
  const [wallets, setWallets] = useState([]);
  const [activeWallet, setActiveWallet] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  // Get current network config based on state
  const networkConfig = useMemo(() => NETWORKS[currentNetwork], [currentNetwork]);

  // Enhanced connectWallet function with automatic network switching
  const connectWallet = async () => {
    if (!currentNetwork) {
      throw new Error('Please select a network first before connecting your wallet.');
    }

    setIsConnecting(true);
    setConnectionStatus('connecting');
    
    try {
      console.log(`Connecting wallet to ${NETWORKS[currentNetwork].name}...`);
      
      const instance = await web3Modal.connect();
      const ethersProvider = new ethers.providers.Web3Provider(instance);
      const signer = ethersProvider.getSigner();
      const address = await signer.getAddress();
      
      console.log(`Wallet connected: ${address}`);
      
      // Get current network
      const network = await ethersProvider.getNetwork();
      const currentChainId = network.chainId;
      const targetChainId = NETWORKS[currentNetwork].chainId;
      
      console.log(`Current chain ID: ${currentChainId}, Target chain ID: ${targetChainId}`);
      
      // Check if we need to switch networks
      if (currentChainId !== targetChainId) {
        console.log(`Switching from chain ${currentChainId} to ${targetChainId}...`);
        try {
          await switchNetwork(ethersProvider.provider, targetChainId);
          
          // Create new provider and signer after network switch
          const newProvider = new ethers.providers.Web3Provider(window.ethereum);
          const newSigner = newProvider.getSigner();
          const newAddress = await newSigner.getAddress();
          
          // Verify we're on the correct network
          const newNetwork = await newProvider.getNetwork();
          if (newNetwork.chainId !== targetChainId) {
            throw new Error(`Failed to switch to ${NETWORKS[currentNetwork].name}. Please try again.`);
          }
          
          console.log(`Successfully switched to ${NETWORKS[currentNetwork].name}`);
          
          // Update provider and signer
          ethersProvider = newProvider;
          signer = newSigner;
          address = newAddress;
        } catch (error) {
          console.error('Network switch error:', error);
          throw new Error(`Failed to switch to ${NETWORKS[currentNetwork].name}: ${error.message}`);
        }
      }
      
      // Check if wallet is already connected
      const existingWallet = wallets.find(w => w.address === address);
      if (!existingWallet) {
        const newWallet = {
          address,
          provider: ethersProvider,
          signer,
          network: await ethersProvider.getNetwork()
        };
        setWallets([...wallets, newWallet]);
      }
      
      setActiveWallet(address);
      setProvider(ethersProvider);
      setSigner(signer);
      setAddress(address);
      setIsConnected(true);
      setConnectionStatus('connected');
      
      // Set contract instance for the selected network
      const contract = new ethers.Contract(NETWORKS[currentNetwork].contractAddress, ABI, signer);
      setContract(contract);
      
      console.log(`Wallet connection successful for ${NETWORKS[currentNetwork].name}`);

    } catch (e) {
      console.error('Failed to connect wallet:', e);
      setConnectionStatus('error');
      setIsConnected(false);
      throw new Error(`Failed to connect wallet: ${e.message}`);
    } finally {
      setIsConnecting(false);
    }
  };

  // Add switch wallet function from working code
  const switchWallet = async (walletAddress) => {
    const wallet = wallets.find(w => w.address === walletAddress);
    if (wallet) {
      setActiveWallet(walletAddress);
      setProvider(wallet.provider);
      setSigner(wallet.signer);
      setAddress(wallet.address);
      setIsConnected(true);
      setConnectionStatus('connected');
      
      // Set contract instance for the selected network
      const contract = new ethers.Contract(networkConfig.contractAddress, ABI, wallet.signer);
      setContract(contract);
    }
  };

  // Update disconnectWallet function from working code
  const disconnectWallet = () => {
    web3Modal.clearCachedProvider();
    setWallets([]);
    setActiveWallet(null);
    setIsConnected(false);
    setAddress("");
    setProvider(null);
    setSigner(null);
    setContract(null);
    setConnectionStatus('disconnected');
  };

  // Enhanced network change handler with better error handling
  const handleNetworkChange = async (newNetwork) => {
    try {
      console.log(`Switching network to: ${NETWORKS[newNetwork].name}`);
      setCurrentNetwork(newNetwork);
      
      if (provider && signer) {
        const targetChainId = NETWORKS[newNetwork].chainId;
        
        try {
          // Get current network
          const currentNetworkInfo = await provider.getNetwork();
          const currentChainId = currentNetworkInfo.chainId;
          
          console.log(`Current chain: ${currentChainId}, Target chain: ${targetChainId}`);
          
          if (currentChainId !== targetChainId) {
            console.log(`Switching from chain ${currentChainId} to ${targetChainId}...`);
            
            // Attempt to switch network
            await switchNetwork(provider.provider, targetChainId);
            
            // Wait for network to be ready
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Create new provider and signer
            const newProviderInstance = new ethers.providers.Web3Provider(window.ethereum);
            const newSignerInstance = newProviderInstance.getSigner();
            
            // Verify we're on the correct network
            const newNetworkInfo = await newProviderInstance.getNetwork();
            if (newNetworkInfo.chainId !== targetChainId) {
              throw new Error(`Failed to switch to ${NETWORKS[newNetwork].name}. Please try again.`);
            }
            
            console.log(`Successfully switched to ${NETWORKS[newNetwork].name}`);
            
            // Update contract for new network
            const contract = new ethers.Contract(NETWORKS[newNetwork].contractAddress, ABI, newSignerInstance);
            setContract(contract);
            setProvider(newProviderInstance);
            setSigner(newSignerInstance);
            setConnectionStatus('connected');
          } else {
            console.log(`Already on correct network: ${NETWORKS[newNetwork].name}`);
            // Already on correct network, just update contract
            const contract = new ethers.Contract(NETWORKS[newNetwork].contractAddress, ABI, signer);
            setContract(contract);
            setConnectionStatus('connected');
          }
        } catch (error) {
          console.error('Network switch error:', error);
          setConnectionStatus('error');
          throw new Error(`Failed to switch to ${NETWORKS[newNetwork].name}: ${error.message}`);
        }
      } else {
        console.log('No wallet connected, just updating network selection');
        // No provider/signer, just update the network selection
        setConnectionStatus('disconnected');
      }
    } catch (error) {
      console.error('Network change error:', error);
      setConnectionStatus('error');
      throw new Error(`Network change failed: ${error.message}`);
    }
  };

  // Enhanced network change listener with better error handling
  useEffect(() => {
    if (window.ethereum) {
      const handleChainChanged = async (chainId) => {
        try {
          console.log(`Chain changed to: ${chainId}`);
          const newChainId = parseInt(chainId, 16);
          const newNetwork = Object.entries(NETWORKS).find(([_, network]) => network.chainId === newChainId)?.[0];
          
          if (newNetwork) {
            console.log(`Detected network change to: ${NETWORKS[newNetwork].name}`);
            
            // Update current network state
            setCurrentNetwork(newNetwork);
            
            // Create new provider and signer for the new network
            const newProvider = new ethers.providers.Web3Provider(window.ethereum);
            const newSigner = newProvider.getSigner();
            
            // Verify the new network
            const networkInfo = await newProvider.getNetwork();
            if (networkInfo.chainId !== newChainId) {
              console.warn(`Network verification failed. Expected ${newChainId}, got ${networkInfo.chainId}`);
            }
            
            // Update contract for new network
            const contract = new ethers.Contract(NETWORKS[newNetwork].contractAddress, ABI, newSigner);
            
            // Update state
            setProvider(newProvider);
            setSigner(newSigner);
            setContract(contract);
            setConnectionStatus('connected');
            
            console.log(`Successfully updated to ${NETWORKS[newNetwork].name}`);
          } else {
            console.warn(`Unsupported network detected: ${newChainId}`);
            setConnectionStatus('error');
          }
        } catch (error) {
          console.error('Chain change error:', error);
          setConnectionStatus('error');
        }
      };

      // Also listen for account changes
      const handleAccountsChanged = (accounts) => {
        console.log('Accounts changed:', accounts);
        if (accounts.length === 0) {
          // User disconnected
          disconnectWallet();
        } else {
          // User switched accounts
          const newAddress = accounts[0];
          setAddress(newAddress);
        }
      };

      window.ethereum.on('chainChanged', handleChainChanged);
      window.ethereum.on('accountsChanged', handleAccountsChanged);

      return () => {
        window.ethereum.removeListener('chainChanged', handleChainChanged);
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      };
    }
  }, []); // Empty dependency array since we want this to run once on mount

  // Add initialization effect with error handling from working code
  useEffect(() => {
    const initialize = async () => {
      try {
        handleEthereumInjection();
        // Check if there's a cached provider
        if (web3Modal.cachedProvider) {
          await connectWallet();
        }
        setIsInitialized(true);
      } catch (error) {
        console.error('Initialization error:', error);
        setInitError(error);
        setIsInitialized(true); // Still set to true to show error state
      }
    };

    initialize();
  }, []); // Empty dependency array means this runs once on mount

  const value = {
    provider,
    signer,
    address,
    isConnected,
    contract,
    currentNetwork,
    networkConfig,
    wallets,
    activeWallet,
    switchWallet,
    handleNetworkChange,
    connectWallet,
    disconnectWallet,
    isConnecting,
    connectionStatus,
    isInitialized,
    initError
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
}; 